"use client";

import React from "react";
import { LineChart } from "lucide-react";

export interface AtrTrajectoryChartBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    chartTitle?: string;
    chartBadge?: string;
    chartPath?: string;
    chartLabels?: string[];
}

export default function AtrTrajectoryChartBlock({
    tag = "// Trajectory Parsing",
    title = "Rebuilding spatial trajectories",
    description = "Below is an altitude profile visualization demonstrating how our systems parse continuous coordinate matrices from FDR sensors, transforming discrete data logs into smooth 3D flight trajectories inside the Unity engine.",
    chartTitle = "Flight Path Altitude Reconstruction",
    chartBadge = "Parsed profile",
    chartPath = "M 10 180 Q 80 180 120 170 T 210 145 T 320 80 T 430 35 T 520 120 T 610 170 L 690 180",
    chartLabels = ["T/O ROLL", "V2 CLIMB", "LEVEL CRUISE", "DESCENT", "FINAL", "TDOWN"]
}: AtrTrajectoryChartBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0C0D0E] border-t border-white/[0.04] w-full" data-header-theme="light">
            <div className="container mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                    
                    <div className="lg:col-span-5 space-y-6">
                        {tag && (
                            <span className="text-xs font-mono uppercase tracking-widest text-[#00E5FF] block">
                                {tag}
                            </span>
                        )}
                        <h2 className="font-cormorant font-serif text-4xl md:text-5xl font-light text-white leading-tight">
                            {title}
                        </h2>
                        {description && (
                            <p className="text-sm text-white/50 leading-relaxed font-light">
                                {description}
                            </p>
                        )}
                    </div>

                    {/* Altitude Graph representation */}
                    <div className="lg:col-span-7 bg-[#111214] border border-white/[0.06] p-6 rounded-xl">
                        <div className="flex items-center justify-between pb-6 border-b border-white/[0.05] text-[10px] font-mono text-white/50 uppercase tracking-wider">
                            <span>{chartTitle}</span>
                            <span className="text-[#00FF66] flex items-center gap-1.5">
                                <LineChart className="w-3.5 h-3.5" /> {chartBadge}
                            </span>
                        </div>

                        <div className="relative w-full h-64 pt-6">
                            {/* SVG Chart */}
                            <svg className="w-full h-full" viewBox="0 0 700 200" preserveAspectRatio="none">
                                {/* Grid Lines */}
                                <line x1="0" y1="180" x2="700" y2="180" className="stroke-white/[0.05]" strokeWidth="1" strokeDasharray="4" />
                                <line x1="0" y1="130" x2="700" y2="130" className="stroke-white/[0.05]" strokeWidth="1" strokeDasharray="4" />
                                <line x1="0" y1="80" x2="700" y2="80" className="stroke-white/[0.05]" strokeWidth="1" strokeDasharray="4" />
                                <line x1="0" y1="30" x2="700" y2="30" className="stroke-white/[0.05]" strokeWidth="1" strokeDasharray="4" />

                                {/* Flight trajectory curve */}
                                <path
                                    d={chartPath}
                                    fill="none"
                                    stroke="url(#gradient-atr-trajectory)"
                                    strokeWidth="3"
                                />

                                {/* SVG Linear Gradient for path */}
                                <defs>
                                    <linearGradient id="gradient-atr-trajectory" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#00FF66" />
                                        <stop offset="50%" stopColor="#00E5FF" />
                                        <stop offset="100%" stopColor="#FFB800" />
                                    </linearGradient>
                                </defs>
                            </svg>
                            
                            {/* Graph X-Axis Labels */}
                            {chartLabels && chartLabels.length > 0 && (
                                <div className="absolute bottom-0 inset-x-0 flex justify-between text-[8px] font-mono text-white/30 pt-2 uppercase">
                                    {chartLabels.map((lbl, idx) => (
                                        <span key={idx}>{lbl}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}
